import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { ChevronDown } from "lucide-react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { canvasToolbarButtonVariants } from "./canvasToolbarStyles";

interface CanvasToolbarButtonProps extends ComponentPropsWithoutRef<"button"> {
  /** Tooltip label; omit to hide tooltip. */
  label?: ReactNode;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  active?: boolean;
  destructive?: boolean;
  accent?: boolean;
  dropdown?: boolean;
  /** Optional mono text label inside the button (e.g. zoom %). */
  monoLabel?: ReactNode;
}

export function CanvasToolbarButton({
  label,
  tooltipSide = "top",
  active = false,
  destructive = false,
  accent = false,
  dropdown = false,
  monoLabel,
  className,
  children,
  disabled,
  ...rest
}: CanvasToolbarButtonProps) {
  const button = (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active ? true : undefined}
      className={cn(
        canvasToolbarButtonVariants({
          active,
          destructive,
          accent,
          hasDropdown: dropdown,
        }),
        className,
      )}
      {...rest}
    >
      <span className="inline-flex shrink-0 items-center justify-center">
        {children}
      </span>
      {monoLabel != null && (
        <span className="font-mono text-xs leading-none">{monoLabel}</span>
      )}
      {dropdown && (
        <span className="inline-flex shrink-0 items-center text-n-50 group-hover:text-n-70">
          <ChevronDown className="size-2.5" strokeWidth={2.5} />
        </span>
      )}
    </button>
  );

  if (!label) {
    return button;
  }

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side={tooltipSide}>{label}</TooltipContent>
    </Tooltip>
  );
}
